import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { useLanguage } from '../LanguageContext';
import ArrowIcon from '../icons/ArrowIcon';
import ClockIcon from '../icons/ClockIcon';
import CrossIcon from '../icons/CrossIcon';
import LocationIcon from '../icons/LocationIcon';

const getGuestName = (guestId, allGuests, t) => {
  const guest = Array.isArray(allGuests)
    ? allGuests.find((g) => String(g.id) === String(guestId))
    : null;
  return guest?.name || `${t('rsvp_form.guest')} ${guestId}`;
};

const buttonStyle = (selected, size = 'lg') => {
  return selected ? 'btn-rsvp-choice-selected' : 'btn-rsvp-choice';
};

const isSelectionDisabled = (guestId, allGuests, guestOptionKey) => {
  if (!guestOptionKey) return false;
  const guest = Array.isArray(allGuests)
    ? allGuests.find((g) => String(g.id) === String(guestId))
    : null;
  return guest ? guest[guestOptionKey] === false : false;
};

const getSpanComponent = (guestId, allGuests, spanOptionKey, spanLabel) => {
  const guest = Array.isArray(allGuests)
    ? allGuests.find((g) => String(g.id) === String(guestId))
    : null;
  if (!guest || !spanOptionKey || !spanLabel) return null;
  return guest[spanOptionKey] ? (
    <span className="text-content-c1-sb border border-rose-accent text-rose-accent text-base font-medium font-semibold px-1.5 py-0.5 rounded">
      {spanLabel}
    </span>
  ) : null;
};

function EventDetailsModal({ event, location, onClose }) {
  const { t } = useLanguage();

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (!event) return null;

  const tScheduleBlock = (k) => t(`schedule.${k}`);
  const displayTitle = tScheduleBlock(event.title);
  const displayTime = tScheduleBlock(event.time);
  const displayDescriptions = tScheduleBlock(event.description)
    .replace(/\\n/g, '\n')
    .split('\n');
  const displayDescriptionTitle = tScheduleBlock(event.descriptionTitleKey);
  const displayVenue = event.venue.map((v) => tScheduleBlock(v));

  return createPortal(
    <div onClick={onClose} className="modal-overlay animate-fade-in">
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ type: 'spring', duration: 0.4 }}
        className="modal-panel text-left space-y-6"
      >
        <button
          onClick={onClose}
          className="modal-close-btn"
          aria-label="Close modal"
        >
          <CrossIcon className="w-6 h-6" />
        </button>

        <div className="text-center space-y-2">
          <h3 className="text-3xl text-content-h1">{displayTitle}</h3>
          <p className="text-xl text-content-c1-sb-num">{displayTime}</p>
        </div>

        <div className="border-t border-wedding-divider pt-4 space-y-3">
          <div className="flex gap-2.5 items-start">
            <LocationIcon className="w-5 h-5 mt-1 flex-shrink-0 text-divider" />
            <div>
              <p className="text-xl text-content-h2">{t('rsvp_form.venue')}</p>
              {displayVenue.map((v, i) => (
                <p key={i} className="text-lg text-content-c2">
                  {v}
                </p>
              ))}
              {location.address && (
                <p className="text-content-c2 mt-1">
                  <a
                    href={location.mapAddress}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {tScheduleBlock(location.address)}
                    <ArrowIcon className="w-4 h-4 inline-block" />
                  </a>
                </p>
              )}
            </div>
          </div>
        </div>

        {displayDescriptions && displayDescriptionTitle && (
          <div className="border-t border-wedding-divider pt-4">
            <p className="text-xl text-content-h2 mb-1">
              {displayDescriptionTitle}
            </p>
            {displayDescriptions.map((desc, i) => (
              <p
                key={i}
                className="text-lg text-content-c2 whitespace-pre-line"
              >
                {desc}
              </p>
            ))}
          </div>
        )}

        {event.timeline && (
          <div className="border-t border-wedding-divider pt-4 space-y-2">
            <p className="text-xl text-content-h2">{t('rsvp_form.timeline')}</p>
            <table className="w-full">
              <tbody>
                {event.timeline.map((line, i) => (
                  <tr
                    key={i}
                    className="border-b last:border-0 border-blush-bg"
                  >
                    <td className="pr-4 align-top text-lg text-table-text-num-b">
                      {line.time}
                    </td>
                    <td className="align-top text-lg text-table-text">
                      {tScheduleBlock(line.event)}
                      {line.subtitle && (
                        <p className="mt-0.5 text-table-text">
                          <i>{tScheduleBlock(line.subtitle)}</i>
                        </p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {location && (
          <div className="border-t border-wedding-divider pt-4 space-y-4">
            <p className="text-xl text-content-h2">
              {t('rsvp_form.getting_there')}
            </p>

            {location.byCar && (
              <div className="space-y-1">
                <p className="text-lg text-content-c2-b">
                  {t('rsvp_form.by_car')}
                </p>
                <p className="text-lg text-content-c2">
                  {tScheduleBlock(location.byCar)}
                </p>
              </div>
            )}

            {location.byTransport && (
              <div className="space-y-2 pt-2">
                <p className="text-lg text-content-c2-b">
                  {t('rsvp_form.by_public_transport')}
                </p>
                <div className="space-y-1 text-md text-lg text-content-c2">
                  <div>
                    <span>{t('rsvp_form.nearest_mrt')}:</span>{' '}
                    {tScheduleBlock(location.byTransport.mrt)}
                  </div>
                  <div>
                    <span>{t('rsvp_form.nearest_bus')}:</span>{' '}
                    {tScheduleBlock(location.byTransport.bus)}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>,
    document.body,
  );
}

export default function IndividualResponsesStep({
  formData,
  setFormData,
  allGuests = [],
  responseKey,
  guestOptionKey,
  title,
  subtitle,
  yesLabel,
  noLabel,
  onNext,
  onBack,
  eventDetails,
  spanOptionKey = null,
  spanLabel = null,
}) {
  const { t } = useLanguage();
  const individualResponses = formData.individualResponses || {};
  const guestIds = Object.keys(individualResponses);
  const hasMultipleGuests = guestIds.length > 1;

  const [showModal, setShowModal] = useState(false);
  const event = eventDetails;
  const location = eventDetails?.location;

  const renderEventDetails = () => {
    if (!event) return null;
    const tScheduleBlock = (k) => t(`schedule.${k}`);
    const bottomBorder = hasMultipleGuests
      ? ''
      : 'border-b border-wedding-divider';
    const displayTime = tScheduleBlock(event.time);
    const displayVenue = event.venue.map((v) => tScheduleBlock(v));

    return (
      <>
        <div className={`py-4 text-left mb-6 ${bottomBorder}`}>
          <div className="flex flex-col items-start justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-lg text-content-c1">
                <ClockIcon className="w-5 h-5" />
                <div>
                  <div className="text-content-c1-sb-num">{displayTime}</div>
                  <div className="text-content-c1-sb">
                    {tScheduleBlock('subtitle')}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2 text-lg text-content-c1">
                <LocationIcon className="w-5 h-5" />
                <div>
                  {displayVenue.map((v, i) => (
                    <div key={i} className="text-content-c1-sb">
                      {v}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="btn-wedding-primary"
            >
              {t('rsvp_form.more_details')}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {showModal && (
            <EventDetailsModal
              event={event}
              location={location}
              onClose={() => setShowModal(false)}
            />
          )}
        </AnimatePresence>
      </>
    );
  };

  const updateGuestResponse = (guestId, value) => {
    setFormData((state) => ({
      ...state,
      individualResponses: {
        ...state.individualResponses,
        [guestId]: {
          ...state.individualResponses?.[guestId],
          [responseKey]: value,
        },
      },
    }));
  };

  const localYesLabel = yesLabel;
  const localNoLabel = noLabel;

  if (!hasMultipleGuests) {
    return (
      <div className="space-y-6 max-w-md mx-auto">
        <div className="space-y-3 text-center">
          <h2 className="flex flex-row text-4xl font-semibold tracking-tight text-wedding-heading">
            <p>
              {title}
              {getSpanComponent(
                guestIds[0],
                allGuests,
                spanOptionKey,
                spanLabel,
              )}
            </p>
          </h2>
        </div>
        {renderEventDetails()}
        <div className="grid gap-4">
          <button
            type="button"
            onClick={() => {
              updateGuestResponse(guestIds[0], true);
              setTimeout(() => onNext(), 500);
            }}
            className={buttonStyle(
              individualResponses[guestIds[0]]?.[responseKey] === true,
            )}
          >
            {localYesLabel}
          </button>
          <button
            type="button"
            onClick={() => {
              updateGuestResponse(guestIds[0], false);
              setTimeout(() => onNext(), 500);
            }}
            className={buttonStyle(
              individualResponses[guestIds[0]]?.[responseKey] === false,
            )}
          >
            {localNoLabel}
          </button>
        </div>
      </div>
    );
  }

  const filteredGuestIds = guestIds.filter(
    (guestId) => !isSelectionDisabled(guestId, allGuests, guestOptionKey),
  );

  if (filteredGuestIds.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3 text-center">
        <h2 className="text-4xl text-content-h1">{title}</h2>
      </div>

      {renderEventDetails()}

      <div className="overflow-x-auto">
        <div className="min-w-full">
          {/* Rows */}
          <div className="space-y-6 divide-y divide-wedding-divider">
            {filteredGuestIds.map((guestId, index) => {
              const response = individualResponses[guestId] || {};
              const value = response[responseKey];

              return (
                <div
                  key={guestId}
                  className={`space-y-3 ${index > 0 ? 'pt-6' : ''}`}
                >
                  <div className="flex flex-row text-2xl text-content-h2 space-x-3">
                    <p>{getGuestName(guestId, allGuests, t)}</p>
                    {getSpanComponent(
                      guestId,
                      allGuests,
                      spanOptionKey,
                      spanLabel,
                    )}
                  </div>

                  <div className="flex flex-row flex-wrap gap-2 text-xl">
                    <button
                      type="button"
                      onClick={() => updateGuestResponse(guestId, true)}
                      className={buttonStyle(value === true, 'lg')}
                    >
                      {localYesLabel}
                    </button>

                    <button
                      type="button"
                      onClick={() => updateGuestResponse(guestId, false)}
                      className={buttonStyle(value === false, 'lg')}
                    >
                      {localNoLabel}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
